'use client'

/**
 * Hook pour gérer la synchronisation URL ↔ projet synoptique
 * - Web : URL propre avec History API (/synoptique/mon-projet-abc123)
 *   - Ouverture depuis le hub : pushState (le bouton Retour revient sur /synoptique)
 *   - Depuis une URL de projet : replaceState (renommage, projet→projet, normalisation)
 *   - Une entrée « garde » est poussée au-dessus de l'entrée projet pour intercepter
 *     le bouton Retour et afficher un avertissement en cas de modifications non sauvegardées
 * - Tauri : Hash routing (#project=abc123)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

interface SynopticUrlState {
  projectId: string | null
  projectSlug: string | null
}

interface SynopticHistoryState {
  synopticType?: 'project' | 'guard'
  synopticIdx?: number
  projectId?: string
  projectName?: string
}

interface UseSynopticUrlOptions {
  /** Retourne true si des modifications sont en attente de sauvegarde */
  shouldBlockExit?: () => boolean
}

/**
 * Génère un slug URL-friendly à partir d'un nom
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^a-z0-9]+/g, '-')     // Remplacer les caractères spéciaux par -
    .replace(/^-+|-+$/g, '')          // Supprimer les - en début/fin
    .substring(0, 50)                 // Limiter la longueur
}

/**
 * Détecte si on est dans Tauri
 */
function isTauriApp(): boolean {
  if (typeof window === 'undefined') return false
  return '__TAURI__' in window || '__TAURI_INTERNALS__' in window
}

/**
 * Extrait l'ID du projet depuis l'URL
 */
function extractProjectIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null

  const isTauri = isTauriApp()

  if (isTauri) {
    // Tauri : Hash routing (#project=abc123)
    const hash = window.location.hash
    const match = hash.match(/project=([a-f0-9-]+)/i)
    return match ? match[1] : null
  } else {
    // Web : Path routing (/synoptique/mon-projet-abc123)
    // Exclure les URLs de partage (/synoptique/share/<token>)
    const path = window.location.pathname
    const match = path.match(/\/synoptique\/(?!share\/)(.+)/)
    if (match) {
      const slug = match[1]
      // Extraire l'ID depuis le slug (format: nom-projet-abc12345)
      // L'ID est les 8 derniers caractères après le dernier tiret
      const parts = slug.split('-')
      const potentialId = parts[parts.length - 1]
      // Vérifier si c'est un UUID partiel (8 caractères hex)
      if (/^[a-f0-9]{8}$/i.test(potentialId)) {
        return potentialId
      }
      // Sinon, c'est peut-être l'ID complet
      if (/^[a-f0-9-]{36}$/i.test(slug)) {
        return slug
      }
    }
    return null
  }
}

/**
 * Vérifie si on est sur la page d'accueil synoptique (sans projet)
 */
function isOnSynoptiqueHome(): boolean {
  if (typeof window === 'undefined') return true

  const path = window.location.pathname
  const hash = window.location.hash

  // Sur /synoptique ou /synoptique/ sans slug ni hash
  const isBasePath = path === '/synoptique' || path === '/synoptique/'
  const hasProjectHash = hash.includes('project=')

  return isBasePath && !hasProjectHash
}

export function useSynopticUrl(options?: UseSynopticUrlOptions) {
  const pathname = usePathname()
  const [urlState, setUrlState] = useState<SynopticUrlState>({
    projectId: null,
    projectSlug: null
  })
  const [isHomePage, setIsHomePage] = useState(true)
  const [exitPromptOpen, setExitPromptOpen] = useState(false)

  // Compteur d'index pour les entrées d'historique créées par le synoptique
  const idxCounterRef = useRef(0)
  // Index de l'entrée courante dans la pile (pour détecter le sens de navigation)
  const positionRef = useRef(0)
  // true quand l'utilisateur a confirmé la sortie : on saute les entrées synoptiques restantes
  const bypassExitRef = useRef(false)
  const shouldBlockExitRef = useRef<(() => boolean) | undefined>(options?.shouldBlockExit)

  useEffect(() => {
    shouldBlockExitRef.current = options?.shouldBlockExit
  }, [options?.shouldBlockExit])

  /**
   * Pousse une entrée d'historique taguée synoptique
   */
  const pushEntry = useCallback((
    type: 'project' | 'guard',
    url: string,
    data?: { projectId?: string; projectName?: string }
  ) => {
    const idx = ++idxCounterRef.current
    const state: SynopticHistoryState = { synopticType: type, synopticIdx: idx, ...data }
    window.history.pushState(state, '', url)
    positionRef.current = idx
  }, [])

  /**
   * Met à jour l'URL quand un projet est chargé
   */
  const updateUrlWithProject = useCallback((project: { id: string; name: string }) => {
    if (typeof window === 'undefined') return

    const isTauri = isTauriApp()
    const shortId = project.id.slice(0, 8)
    const slug = `${slugify(project.name)}-${shortId}`

    setUrlState({
      projectId: project.id,
      projectSlug: slug
    })
    setIsHomePage(false)
    bypassExitRef.current = false

    if (isTauri) {
      // Tauri : Hash routing (compatible avec refresh)
      const newHash = `#project=${project.id}`
      if (window.location.hash !== newHash) {
        window.location.hash = newHash
      }
      return
    }

    // Web : URL propre avec History API
    const newPath = `/synoptique/${slug}/`
    const currentState = window.history.state as SynopticHistoryState | null

    if (extractProjectIdFromUrl() !== null) {
      // Déjà sur une URL de projet (renommage, projet→projet, normalisation après
      // chargement direct) : remplacer l'entrée courante en préservant type/index
      const state: SynopticHistoryState = {
        synopticType: currentState?.synopticType ?? 'project',
        synopticIdx: currentState?.synopticIdx ?? ++idxCounterRef.current,
        projectId: project.id,
        projectName: project.name
      }
      window.history.replaceState(state, '', newPath)
      if (state.synopticIdx !== undefined) {
        positionRef.current = state.synopticIdx
      }
    } else {
      // Depuis le hub : pousser une nouvelle entrée pour que le bouton Retour
      // revienne sur /synoptique
      pushEntry('project', newPath, { projectId: project.id, projectName: project.name })
    }

    // Garantir une entrée « garde » au sommet pour intercepter le bouton Retour
    const topState = window.history.state as SynopticHistoryState | null
    if (topState?.synopticType !== 'guard') {
      pushEntry('guard', newPath, { projectId: project.id })
    }
  }, [pushEntry])

  /**
   * Efface l'URL (retour à /synoptique)
   */
  const clearProjectUrl = useCallback(() => {
    if (typeof window === 'undefined') return

    setUrlState({
      projectId: null,
      projectSlug: null
    })
    setIsHomePage(true)
    setExitPromptOpen(false)

    const isTauri = isTauriApp()

    if (isTauri) {
      window.location.hash = ''
    } else {
      window.history.replaceState({}, '', '/synoptique/')
    }
  }, [])

  /**
   * Confirme la sortie du projet (bouton « Partir » du popup d'avertissement)
   */
  const confirmExit = useCallback(() => {
    bypassExitRef.current = true
    setExitPromptOpen(false)
    window.history.back()
  }, [])

  /**
   * Annule la sortie : reste sur le projet et re-arme l'entrée garde
   */
  const cancelExit = useCallback(() => {
    setExitPromptOpen(false)
    if (typeof window === 'undefined' || isTauriApp()) return
    const currentState = window.history.state as SynopticHistoryState | null
    pushEntry('guard', window.location.pathname, { projectId: currentState?.projectId })
  }, [pushEntry])

  /**
   * Récupère l'ID du projet depuis l'URL au chargement
   */
  const getProjectIdFromUrl = useCallback((): string | null => {
    return extractProjectIdFromUrl()
  }, [])

  /**
   * Écoute les changements d'URL (popstate pour Web, hashchange pour Tauri)
   * `pathname` est dans les deps pour recalculer après les navigations Next.js
   * (clics sur des <Link>, qui ne déclenchent pas popstate)
   */
  useEffect(() => {
    if (typeof window === 'undefined') return

    const refreshFromUrl = () => {
      const projectId = extractProjectIdFromUrl()
      setUrlState(prev => ({
        ...prev,
        projectId
      }))
      setIsHomePage(isOnSynoptiqueHome())
      if (projectId === null) {
        setExitPromptOpen(false)
      }
    }

    const handlePopState = () => {
      const st = window.history.state as SynopticHistoryState | null
      const newIdx = st?.synopticIdx ?? 0
      const goingBack = newIdx < positionRef.current
      positionRef.current = newIdx

      if (goingBack) {
        if (bypassExitRef.current) {
          // Sortie confirmée : sauter les entrées synoptiques restantes
          if (st?.synopticType === 'project' || st?.synopticType === 'guard') {
            window.history.back()
            return
          }
          bypassExitRef.current = false
        } else if (st?.synopticType === 'project') {
          // L'utilisateur quitte le projet via le bouton Retour
          if (shouldBlockExitRef.current?.()) {
            // Modifications en attente : afficher le popup d'avertissement
            setExitPromptOpen(true)
          } else {
            // Rien à sauvegarder : continuer vers /synoptique
            window.history.back()
            return
          }
        } else if (st?.synopticType === 'guard') {
          // Entrée garde orpheline (projet refermé entre-temps) : la sauter
          window.history.back()
          return
        }
      }

      refreshFromUrl()
    }

    // Initialiser l'état et la position courante
    const initialState = window.history.state as SynopticHistoryState | null
    if (initialState?.synopticIdx !== undefined) {
      positionRef.current = initialState.synopticIdx
      if (initialState.synopticIdx > idxCounterRef.current) {
        idxCounterRef.current = initialState.synopticIdx
      }
    }
    refreshFromUrl()

    // Écouter les changements
    window.addEventListener('popstate', handlePopState)
    window.addEventListener('hashchange', refreshFromUrl)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('hashchange', refreshFromUrl)
    }
  }, [pathname])

  return {
    projectIdFromUrl: urlState.projectId,
    projectSlug: urlState.projectSlug,
    isOnHomePage: isHomePage,
    updateUrlWithProject,
    clearProjectUrl,
    getProjectIdFromUrl,
    isTauriApp: isTauriApp(),
    exitPromptOpen,
    confirmExit,
    cancelExit
  }
}
